/**
 * What never leaves for Sentry: the token of a password link ("/user/reset_password/<id>/
 * <token>"), the admin hub's hand-off token (`?customer_token=`), any other token in a URL
 * and the value of every secret key. Error events, page loads, breadcrumbs and session
 * replays all carry the page's URL.
 */

/** Keys whose value is a secret, wherever they stand in an event. */
const SECRET_KEY = /password|passwd|token|authorization|cookie|secret|signature|api[-_]?key|^otp/i

/** A text with the secrets a URL can carry replaced by "[Filtered]". */
export function scrubUrl(text: string): string {
  return text
    .replace(/(\/reset_password\/\d+\/)[^/?#\s]+/g, '$1[Filtered]')
    .replace(/([?&](?:[\w-]*token|signature)=)[^&#\s]*/gi, '$1[Filtered]')
}

/**
 * A copy of `value` with every secret replaced: secret keys at any depth, URLs in texts. An
 * object met twice (or in a loop) gets the same scrubbed copy both times, never itself.
 */
export function scrubSecrets<T>(value: T, copies = new WeakMap<object, unknown>()): T {
  if (typeof value === 'string') return scrubUrl(value) as T
  if (!value || typeof value !== 'object') return value
  if (copies.has(value)) return copies.get(value) as T
  if (Array.isArray(value)) {
    const items: unknown[] = []
    copies.set(value, items)
    for (const item of value) items.push(scrubSecrets(item, copies))
    return items as T
  }
  const copy: Record<string, unknown> = {}
  copies.set(value, copy)
  for (const [key, item] of Object.entries(value)) {
    copy[key] = SECRET_KEY.test(key) ? '[Filtered]' : scrubSecrets(item, copies)
  }
  return copy as T
}
